import { Marker } from "react-leaflet";
import { destinationPoint } from "../map/geo";
import { aircraftIcon } from "../map/icons";
import type { Aircraft } from "../map/types";

// Past this, the server has stopped hearing from the aircraft and the marker is a guess at
// where it used to be. It fades rather than vanishing, because "we lost it" is information.
const STALE_AFTER_S = 60;

// Markers glide between the 5-second server ticks by dead-reckoning from the last reported
// position along track at ground speed. Capped: past this the extrapolation is a guess, and a
// marker drifting on for a minute after the feed lost the aircraft would be inventing a flight.
const GLIDE_CAP_S = 20;
const KT_TO_MS = 1852 / 3600;

function displayedPosition(a: Aircraft, ageS: number): [number, number] {
  if (a.ground || a.track === undefined || a.gs === undefined || a.gs < 30) return [a.lat, a.lon];
  return destinationPoint(a.lat, a.lon, a.track, a.gs * KT_TO_MS * Math.min(ageS, GLIDE_CAP_S));
}

export default function AircraftLayer({
  aircraft,
  ageOffsetS,
  selectedHex,
  onSelect,
  showLabels,
}: {
  aircraft: Aircraft[];
  /** Seconds elapsed since the snapshot arrived, so markers keep ageing between ticks. */
  ageOffsetS: number;
  selectedHex: string | null;
  onSelect: (hex: string) => void;
  /** Callsign labels, drawn inside each marker (see `label` in map/icons.ts for why). */
  showLabels: boolean;
}) {
  return (
    <>
      {aircraft.map((a) => {
        const age = a.age + ageOffsetS;
        return (
          <Marker
            key={a.hex}
            position={displayedPosition(a, age)}
            icon={aircraftIcon({
              track: a.track,
              alt: a.alt,
              ground: a.ground,
              selected: a.hex === selectedHex,
              stale: age > STALE_AFTER_S,
              label: showLabels ? (a.flight ?? a.reg) : undefined,
            })}
            // Selected aircraft on top: otherwise the one you just tapped can sit under a
            // neighbour and its highlight ring is invisible.
            zIndexOffset={a.hex === selectedHex ? 1000 : 0}
            eventHandlers={{ click: () => onSelect(a.hex) }}
          />
        );
      })}
    </>
  );
}
